import React, { useEffect, useState } from 'react';

import { IServerOrder } from '@rbi-ctg/menu';
import LoadingAnimation from 'components/loading-animation';
import {
  IGenerateFirstpayCheckoutLinkMutation,
  useGenerateFirstpayCheckoutLinkMutation,
} from 'generated/rbi-graphql';
import { usePaymentContext } from 'state/payment';
import logger from 'utils/logger';

import { OutcomeResult } from '../types';

import { FirstpayHostedPaymentContainer } from './firstpay-hosted-payment-form.styled';

export interface IFirstpayHostedPaymentFormProps {
  rbiOrder: IServerOrder;
  onResult: (outcome: OutcomeResult) => void;
}

type ResultAPI = {
  link: string;
};

export const FirstpayHostedPaymentForm: React.FC<IFirstpayHostedPaymentFormProps> = ({
  rbiOrder,
  onResult,
}) => {
  const [paymentLink, setPaymentLink] = useState('');
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState('850px');
  const { paymentValues } = usePaymentContext();

  const handleFirstpayLinkCompleted = (result: IGenerateFirstpayCheckoutLinkMutation) => {
    setPaymentLink(result.generateFirstpayCheckoutLink.link);
  };

  const [generateFirstpayLink] = useGenerateFirstpayCheckoutLinkMutation({
    onCompleted: handleFirstpayLinkCompleted,
    onError: error => {
      logger.error({
        message: 'Firstpay: Non-vaulted CC integration threw an error',
        error,
      });

      throw error;
    },
  });

  useEffect(() => {
    if (rbiOrder?.rbiOrderId) {
      generateFirstpayLink({
        variables: { rbiOrderId: rbiOrder.rbiOrderId },
      });
    }
  }, [generateFirstpayLink, paymentValues.accountIdentifier, rbiOrder]);

  useEffect(() => {
    if (!paymentLink) {
      return undefined;
    }

    function messageListenerFn(event: MessageEvent) {
      const origin = new URL(event.origin);

      if (!origin.hostname.endsWith('.rbictg.com')) {
        return;
      }
      if (event.data.type !== 'payment-outcome') {
        return;
      }

      const body = event.data.body as OutcomeResult;

      setIframeHeight('0px');
      onResult(body);
    }

    window.addEventListener('message', messageListenerFn);
    return () => window.removeEventListener('message', messageListenerFn);
  }, [paymentLink, onResult]);

  return (
    <FirstpayHostedPaymentContainer style={{ height: iframeHeight }}>
      {!paymentLink && !iframeLoaded && <LoadingAnimation fillColor={Styles.color.primary} />}
      {paymentLink && (
        <iframe
          onLoad={() => setIframeLoaded(true)}
          data-testid="hosted-payment-form"
          title="Payment Form"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            visibility: iframeLoaded ? 'visible' : 'hidden',
          }}
          src={paymentLink}
        ></iframe>
      )}
    </FirstpayHostedPaymentContainer>
  );
};
